import { LitElement, html, css } from 'lit';

/**
 * CrusherAvatarMenu
 *
 * Avatar-anchored profile dropdown for SaaS app shells. Renders an avatar
 * button (image or generated initials) that opens a panel with a user-info
 * block at the top and consumer-provided menu items below.
 *
 * Attributes:
 *   - name        (string) , required; user's display name (used for the info block + initials fallback)
 *   - email       (string) , optional; shown beneath the name in the info block
 *   - avatar-url  (string) , optional; if set, renders <img>; otherwise renders initials derived from `name`
 *   - placement   (enum): "bottom-end" (default) | "bottom-start" | "top-end" | "top-start"
 *   - open        (boolean), reflects open state; consumer can set programmatically
 *
 * Slots:
 *   - default, menu items (any clickable: <button>, <a>, dividers via <hr>)
 *   - footer , pinned at the bottom of the panel (typical: a sign-out button)
 *
 * Events:
 *   - toggle, { open: boolean } whenever the open state changes
 *
 * A11y:
 *   - Trigger has aria-haspopup="menu" + aria-expanded + accessible name from `name`
 *   - Panel has role="menu"
 *   - Closes on Escape (focus returns to trigger), outside-click, and slotted-item click
 *
 * Note: this component is intentionally smaller than crusher-dropdown, no
 * keyboard fuzzy-search, no value reflection. Designed for the "click avatar
 * → see profile + sign out" pattern, not generic select.
 */
export class CrusherAvatarMenu extends LitElement {
  static properties = {
    name:      { type: String, reflect: true },
    email:     { type: String, reflect: true },
    avatarUrl: { type: String, attribute: 'avatar-url', reflect: true },
    placement: { type: String, reflect: true },
    open:      { type: Boolean, reflect: true }
  };

  constructor() {
    super();
    this.name = '';
    this.email = '';
    this.avatarUrl = '';
    this.placement = 'bottom-end';
    this.open = false;
    this._onDocPointer = this._onDocPointer.bind(this);
    this._onDocKey = this._onDocKey.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this._onDocPointer, true);
    document.addEventListener('keydown', this._onDocKey, true);
  }
  disconnectedCallback() {
    document.removeEventListener('pointerdown', this._onDocPointer, true);
    document.removeEventListener('keydown', this._onDocKey, true);
    super.disconnectedCallback();
  }

  static styles = css`
    :host { position: relative; display: inline-block; }

    .trigger {
      appearance: none;
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      background: var(--crusher-surface);
      color: var(--crusher-fg);
      width: var(--crusher-spacing-8);
      height: var(--crusher-spacing-8);
      border-radius: var(--crusher-radius-full);
      padding: 0;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      font-weight: var(--crusher-font-weight-semibold);
      font-size: var(--crusher-font-size-sm);
      transition: box-shadow var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard),
                  border-color var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard);
    }
    .trigger:hover {
      border-color: color-mix(in srgb, var(--crusher-color-brand-primary) 50%, var(--crusher-border));
    }
    .trigger:focus-visible {
      outline: var(--crusher-focus-width) solid var(--crusher-color-brand-primary);
      outline-offset: var(--crusher-focus-offset);
    }
    .trigger img {
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
    }

    .panel {
      position: absolute;
      z-index: var(--crusher-z-dropdown);
      min-width: 14rem;
      max-width: 20rem;
      background: var(--crusher-surface);
      color: var(--crusher-fg);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-lg);
      box-shadow: var(--crusher-shadow-md, var(--crusher-shadow-2));
      padding: var(--crusher-spacing-1);
      display: none;
      flex-direction: column;
      overflow: hidden;
    }
    :host([open]) .panel { display: flex; }

    /* placement */
    :host([placement="bottom-end"]) .panel  { top: calc(100% + var(--crusher-spacing-2)); inset-inline-end: 0; }
    :host([placement="bottom-start"]) .panel { top: calc(100% + var(--crusher-spacing-2)); inset-inline-start: 0; }
    :host([placement="top-end"]) .panel     { bottom: calc(100% + var(--crusher-spacing-2)); inset-inline-end: 0; }
    :host([placement="top-start"]) .panel   { bottom: calc(100% + var(--crusher-spacing-2)); inset-inline-start: 0; }

    .user-info {
      padding: var(--crusher-spacing-2) var(--crusher-spacing-3);
      border-bottom: var(--crusher-component-border-weight) solid var(--crusher-border);
      margin-bottom: var(--crusher-spacing-1);
    }
    .user-info .name {
      font-weight: var(--crusher-font-weight-semibold);
      font-size: var(--crusher-font-size-sm);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .user-info .email {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-xs);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .items, .footer {
      display: flex;
      flex-direction: column;
      padding: var(--crusher-spacing-1);
      gap: var(--crusher-spacing-1);
    }
    .footer {
      border-top: var(--crusher-component-border-weight) solid var(--crusher-border);
      margin-top: var(--crusher-spacing-1);
    }

    /* Style slotted clickable items consistently. The consumer slots in
       buttons/anchors; we apply baseline menu-item visuals. */
    .items ::slotted(*), .footer ::slotted(*) {
      appearance: none;
      background: transparent;
      color: inherit;
      border: none;
      text-align: start;
      font: inherit;
      padding: var(--crusher-spacing-2) var(--crusher-spacing-3);
      border-radius: var(--crusher-radius-sm);
      cursor: pointer;
      text-decoration: none;
      display: block;
      width: 100%;
      box-sizing: border-box;
    }
    .items ::slotted(*:hover),
    .footer ::slotted(*:hover) {
      background: color-mix(in srgb, var(--crusher-fg) 6%, transparent);
    }
    .items ::slotted(hr) {
      border: none;
      border-top: var(--crusher-component-border-weight) solid var(--crusher-border);
      margin: var(--crusher-spacing-1) 0;
      padding: 0;
      pointer-events: none;
      cursor: default;
    }
  `;

  _initials() {
    const n = (this.name || '').trim();
    if (!n) return '?';
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  _toggle(force) {
    const next = typeof force === 'boolean' ? force : !this.open;
    if (next === this.open) return;
    this.open = next;
    this.dispatchEvent(new CustomEvent('toggle', { detail: { open: this.open } }));
  }

  _onTriggerClick() {
    this._toggle();
  }

  _onPanelClick(e) {
    /* Close when a slotted clickable item is activated. Don't close on
       slotted dividers (hr) or non-clickable children. */
    const target = e.target;
    if (!target) return;
    const isClickable = target.tagName === 'BUTTON' || target.tagName === 'A' ||
      (typeof target.matches === 'function' && target.matches('[role="menuitem"]'));
    if (isClickable) this._toggle(false);
  }

  _onDocPointer(e) {
    if (!this.open) return;
    /* Composed path tells us whether the click was inside the component. */
    const path = e.composedPath ? e.composedPath() : [];
    if (path.includes(this)) return;
    this._toggle(false);
  }

  _onDocKey(e) {
    if (!this.open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this._toggle(false);
      /* Return focus to the trigger button. */
      const trigger = this.renderRoot.querySelector('.trigger');
      if (trigger) trigger.focus();
    }
  }

  render() {
    const initials = this._initials();
    const triggerLabel = this.name ? `Open menu for ${this.name}` : 'Open menu';
    return html`
      <button
        class="trigger"
        type="button"
        aria-haspopup="menu"
        aria-expanded=${this.open ? 'true' : 'false'}
        aria-label=${triggerLabel}
        @click=${this._onTriggerClick}>
        ${this.avatarUrl
          ? html`<img src=${this.avatarUrl} alt="" />`
          : html`<span aria-hidden="true">${initials}</span>`}
      </button>
      <div class="panel" role="menu" aria-label="Profile menu" @click=${this._onPanelClick}>
        ${this.name
          ? html`<div class="user-info">
              <div class="name">${this.name}</div>
              ${this.email ? html`<div class="email">${this.email}</div>` : null}
            </div>`
          : null}
        <div class="items"><slot></slot></div>
        <div class="footer"><slot name="footer"></slot></div>
      </div>
    `;
  }
}

customElements.define('crusher-avatar-menu', CrusherAvatarMenu);
